import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import dayjs from "dayjs";
import { Tooltip, Spin } from "antd";
import {
  convertBusinessUTCToLocalDayjs,
  convertLocalDayjsToBusinessUTC,
} from "../../../helpers/moment";
import { useAppDispatch } from "../../../store";
import { getCalendarEventsAPI } from "../TeamMembers/teamMembersApis";
import { getDecryptedUserData } from "../../../helpers/bycrypt";
import CalendarEventDetailsModal from "./CalendarEventDetailsModal";
import "./Calendar.css";
import { svg } from "../../../Assets/svg";
import { useNavigate } from "react-router-dom";
import { getSystemStartYear } from "../../../helpers/utils";

export enum CalendarEventTypesEnum {
  NON_WORKING_DAY = "non_working_day",
  TIME_OFF_REQUEST = "time_off_request",
  HOLIDAY = "holiday",
}

// Crisp Beach Umbrella SVG matching the mobile reference snap
const UmbrellaIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 17,
  color = "#ffffff",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <g transform="rotate(-15 12 12)">
      <line
        x1="12"
        y1="2"
        x2="12"
        y2="4"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M3 13C3 8.02944 7.02944 4 12 4C16.9706 4 21 8.02944 21 13C19.5 12 17.5 12 16 13C14.5 12 12.5 12 11 13C9.5 12 7.5 12 6 13C4.5 12 3.5 12.5 3 13Z"
        fill={color}
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M12 13V19C12 20.1046 12.8954 21 14 21C15.1046 21 16 20.1046 16 19"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

// Crisp Holiday / Star SVG for holiday events
const HolidayIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = "#ffffff",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M12 2L14.8 8.2L21.5 8.9L16.4 13.5L17.9 20.1L12 16.6L6.1 20.1L7.6 13.5L2.5 8.9L9.2 8.2L12 2Z" />
  </svg>
);

const WEEKDAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface SingleMonthCalendarProps {
  monthDate: dayjs.Dayjs;
  events: any[];
  onEventClick: (event: any) => void;
  monthRef?: React.Ref<HTMLDivElement>;
}

const SingleMonthCalendar: React.FC<SingleMonthCalendarProps> = ({
  monthDate,
  events,
  onEventClick,
  monthRef,
}) => {
  const daysInMonth = monthDate.daysInMonth();
  const firstDayOfMonth = monthDate.startOf("month");

  // Sunday is 0, Monday is 1, ..., Saturday is 6
  // We want week to start on Monday (Mon = 0, Sun = 6)
  const firstDayWeekdayIndex = (firstDayOfMonth.day() + 6) % 7;
  const today = dayjs();
  const todayStr = today.format("YYYY-MM-DD");
  const isCurrentMonth =
    monthDate.isSame(today, "month") && monthDate.isSame(today, "year");

  const weeks = useMemo(() => {
    const cells: Array<{
      dayNumber: number | null;
      dateStr: string | null;
      isWeekend: boolean;
      isToday: boolean;
      colIndex: number;
    }> = [];

    // Leading empty cells
    for (let i = 0; i < firstDayWeekdayIndex; i++) {
      cells.push({
        dayNumber: null,
        dateStr: null,
        isWeekend: i === 5 || i === 6,
        isToday: false,
        colIndex: i,
      });
    }

    // Days of month
    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = monthDate.date(d);
      const dateStr = cellDate.format("YYYY-MM-DD");
      const colIndex = (firstDayWeekdayIndex + (d - 1)) % 7;
      const isWeekend = colIndex === 5 || colIndex === 6;
      const isToday = dateStr === todayStr;

      cells.push({
        dayNumber: d,
        dateStr,
        isWeekend,
        isToday,
        colIndex,
      });
    }

    // Trailing empty cells to fill the last week
    while (cells.length % 7 !== 0) {
      const colIndex = cells.length % 7;
      cells.push({
        dayNumber: null,
        dateStr: null,
        isWeekend: colIndex === 5 || colIndex === 6,
        isToday: false,
        colIndex,
      });
    }

    // Group into 7-day rows
    const groupedWeeks: (typeof cells)[] = [];
    for (let i = 0; i < cells.length; i += 7) {
      groupedWeeks.push(cells.slice(i, i + 7));
    }
    return groupedWeeks;
  }, [monthDate, daysInMonth, firstDayWeekdayIndex, todayStr]);

  return (
    <div ref={monthRef} className="single-month-wrapper">
      {/* Month Name */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div
          className={`month-name-heading mb-0 ${
            isCurrentMonth ? "text-primary" : ""
          }`}
        >
          {monthDate.format("MMMM")}
        </div>
        {isCurrentMonth && (
          <span
            className="badge rounded-pill bg-primary-subtle text-primary fw-semibold px-2 py-1"
            style={{ fontSize: "0.72rem" }}
          >
            Current
          </span>
        )}
      </div>

      {/* Weekday headers: Mon Tue Wed Thu Fri Sat Sun */}
      <div className="weekdays-header-row">
        {WEEKDAY_NAMES.map((name) => (
          <div key={name} className="weekday-header-cell">
            {name}
          </div>
        ))}
      </div>

      {/* Weeks container */}
      <div className="month-weeks-container">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="week-row">
            {week.map((cell, cellIdx) => {
              if (cell.dayNumber === null || !cell.dateStr) {
                return (
                  <div key={cellIdx} className="day-cell">
                    <div style={{ width: 34, height: 34 }} />
                  </div>
                );
              }

              // Check if any events fall on this day
              const dayEvents = events.filter((e) => {
                if (!e.start || !e.end) return false;
                return cell.dateStr! >= e.start && cell.dateStr! <= e.end;
              });

              if (dayEvents.length > 0) {
                return (
                  <div key={cellIdx} className="day-cell">
                    {dayEvents.map((event, idx) => {
                      const isMultiple = dayEvents.length > 1;

                      let segmentClass = "seg-circle";
                      if (isMultiple) {
                        const isSegStart =
                          cell.dateStr === event.start ||
                          cell.colIndex === 0 ||
                          cell.dayNumber === 1;
                        const isSegEnd =
                          cell.dateStr === event.end ||
                          cell.colIndex === 6 ||
                          cell.dayNumber === daysInMonth;

                        if (isSegStart && isSegEnd) segmentClass = "seg-single";
                        else if (isSegStart) segmentClass = "seg-start";
                        else if (isSegEnd) segmentClass = "seg-end";
                        else segmentClass = "seg-middle";
                      }

                      const tooltipText = `${
                        event.text ||
                        (event.type === CalendarEventTypesEnum.HOLIDAY
                          ? "Holiday"
                          : "Time Off")
                      } (${dayjs(event.start).format("DD MMM")} – ${dayjs(
                        event.end,
                      ).format("DD MMM YYYY")})`;

                      return (
                        <Tooltip key={event.id || idx} title={tooltipText}>
                          <div
                            className={`event-ribbon-segment ${segmentClass}`}
                            style={{
                              backgroundColor:
                                event.backColor ||
                                (event.type === CalendarEventTypesEnum.HOLIDAY
                                  ? "#e91e63"
                                  : "#a82d6c"),
                              ...(isMultiple
                                ? {
                                    top: idx === 0 ? "2px" : "21px",
                                    bottom: idx === 0 ? "21px" : "2px",
                                    height: "18px",
                                  }
                                : {}),
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick(event);
                            }}
                          >
                            {event.type === CalendarEventTypesEnum.HOLIDAY ? (
                              <HolidayIcon size={isMultiple ? 12 : 16} />
                            ) : (
                              <UmbrellaIcon size={isMultiple ? 12 : 17} />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              }

              // Normal Day Cell (No Events)
              return (
                <div key={cellIdx} className="day-cell">
                  {cell.isWeekend ? (
                    <div
                      className={`day-weekend-circle ${
                        cell.isToday ? "day-today-indicator" : ""
                      }`}
                    >
                      {cell.dayNumber}
                    </div>
                  ) : (
                    <div
                      className={`day-number-text ${
                        cell.isToday ? "day-today-indicator" : ""
                      }`}
                    >
                      {cell.dayNumber}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const Calendar = ({ isTab = false }: { isTab?: boolean }) => {
  const dispatch = useAppDispatch();
  const user = getDecryptedUserData();

  const navigate = useNavigate();
  // Selected year (defaults to current year)
  const [selectedYear, setSelectedYear] = useState<number>(() =>
    dayjs().year(),
  );

  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [detailsModalState, setDetailsModalState] = useState<{
    id: string | null;
    type: CalendarEventTypesEnum | null;
  }>({
    id: null,
    type: null,
  });

  // Fetch full year of events whenever selectedYear changes
  const fetchYearCalendarEvents = (year: number) => {
    setLoading(true);
    const startDate = convertLocalDayjsToBusinessUTC(
      dayjs().year(year).startOf("year"),
    );
    const endDate = convertLocalDayjsToBusinessUTC(
      dayjs().year(year).endOf("year"),
    );
    if (!startDate || !endDate) {
      setLoading(false);
      return;
    }

    dispatch(
      getCalendarEventsAPI({
        filters: {
          employeeId: user?._id,
          startDate: startDate,
          endDate: endDate,
        },
        onSuccess: (data: any) => {
          if (data && data.calendar_days) {
            const timezoneSafeEvents = data.calendar_days
              .map((event: any) => ({
                ...event,
                start: convertBusinessUTCToLocalDayjs(event.start)?.format(
                  "YYYY-MM-DD",
                ),
                end: convertBusinessUTCToLocalDayjs(event.end)?.format(
                  "YYYY-MM-DD",
                ),
              }))
              .filter(
                (item: any) =>
                  item?.type !== CalendarEventTypesEnum.NON_WORKING_DAY,
              );
            setEvents(timezoneSafeEvents);
          }
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    fetchYearCalendarEvents(selectedYear);
  }, [selectedYear]);

  // All 12 months for the selected year
  const allMonths = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) =>
      dayjs().year(selectedYear).month(i).startOf("month"),
    );
  }, [selectedYear]);

  const currentMonthRef = useRef<HTMLDivElement | null>(null);

  const scrollToCurrentMonth = (behavior: ScrollBehavior = "smooth") => {
    if (currentMonthRef.current) {
      currentMonthRef.current.scrollIntoView({
        behavior,
        block: "start",
      });
    }
  };

  // Automatically scroll to the Current month by default (especially on mobile)
  useLayoutEffect(() => {
    if (!loading && selectedYear === dayjs().year()) {
      scrollToCurrentMonth("auto");
    }
  }, [loading, selectedYear]);

  const startYear = getSystemStartYear();
  const isPrevDisabled = selectedYear <= startYear;

  // Navigation handlers
  const handlePrevious = () => {
    if (isPrevDisabled) return;
    setSelectedYear((prev) => prev - 1);
  };

  const handleNext = () => {
    setSelectedYear((prev) => prev + 1);
  };

  const handleToday = () => {
    const currentYear = dayjs().year();
    if (selectedYear === currentYear) {
      scrollToCurrentMonth("smooth");
    } else {
      setSelectedYear(currentYear);
    }
  };

  const handleEventClick = (event: any) => {
    const eventId = event?.id;
    const eventType = event?.type;
    if (
      !eventId ||
      !eventType ||
      [CalendarEventTypesEnum.NON_WORKING_DAY].includes(eventType)
    ) {
      return;
    }
    setDetailsModalState({ id: eventId, type: eventType });
  };

  return (
    <>
      <div
        className={isTab ? "h-100":"page-content h-100 px-40 py-40 mobile-font-s" }
      >
        {isTab ? (
          ""
        ) : (
          <div className={`d-flex flex-column gap-md-2 gap-0 mb-3`}>
            <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-sm-center align-items-start">
              <div className="d-flex align-items-sm-center align-items-start gap-md-3 gap-2 order-md-1 order-1 w-mob-85">
                <span
                  onClick={() => {
                    navigate(-1);
                  }}
                  className="svg-24 cursor-pointer"
                >
                  {svg?.backArrow}
                </span>
                <h2 className="fw-bold page-title mb-0 d-flex flex-wrap align-items-sm-center gap-2">
                  Calendar
                </h2>
              </div>
            </div>
          </div>
        )}
        <div className="custom-calendar-card position-relative d-flex flex-column h-100 overflow-hidden">
          {/* Loading overlay */}
          {loading && (
            <div className="calendar-loading-overlay">
              <Spin size="large" />
            </div>
          )}

          {/* Calendar Top Navigation Header */}
          <div className="calendar-top-header">
            {/* Year & Range Display */}
            <div className="d-flex flex-wrap align-items-baseline gap-1">
              <h2 className="calendar-year-title">{selectedYear}</h2>
              <span className="calendar-months-range-label">All 12 Months</span>
            </div>

            {/* Navigation Buttons: Prev, Today, Next */}
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className={`calendar-nav-btn ${isPrevDisabled ? "disabled pe-none opacity-50" : ""}`}
                onClick={isPrevDisabled ? undefined : handlePrevious}
                disabled={isPrevDisabled}
                title={isPrevDisabled ? `Cannot navigate before ${startYear}` : "Previous Year"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              <button
                type="button"
                className="calendar-today-btn"
                onClick={handleToday}
              >
                Today
              </button>

              <button
                type="button"
                className="calendar-nav-btn"
                onClick={handleNext}
                title="Next Year"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>

          {/* 12 Months Yearly Grid */}
          <div className="calendar-yearly-grid flex-fill overflow-y-auto">
            {allMonths.map((m) => {
              const isCurrent =
                m.isSame(dayjs(), "month") && m.isSame(dayjs(), "year");
              return (
                <SingleMonthCalendar
                  key={m.format("YYYY-MM")}
                  monthDate={m}
                  events={events}
                  onEventClick={handleEventClick}
                  monthRef={isCurrent ? currentMonthRef : undefined}
                />
              );
            })}
          </div>
        </div>

        {/* Event Details Modal */}
        {detailsModalState?.id !== null && detailsModalState?.type !== null && (
          <CalendarEventDetailsModal
            open={detailsModalState?.id !== null}
            id={detailsModalState?.id}
            eventType={detailsModalState?.type}
            onClose={() => {
              setDetailsModalState({ id: null, type: null });
            }}
          />
        )}
      </div>
    </>
  );
};

export default Calendar;
